<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import schedulingAPI from '../api/scheduling';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  patientId: {
    type: [Number, String],
    default: null,
  },
  patientData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'close',
  'edit-patient',
  'discharge-patient',
  'block-patient',
  'reopen-treatment',
  'unblock-patient',
  'updated',
]);

const activeTab = ref('timeline'); // 'timeline' | 'prontuario' | 'enrollments' | 'contacts'
const timelineFilter = ref('all'); // 'all' | 'completed' | 'cancelled' | 'future'
const isSavingProntuario = ref(false);
const localMedicalRecord = ref('');
const newContactPhone = ref('');
const newContactName = ref('');
const newContactRel = ref('Familiar / Responsável');
const isAddingContact = ref(false);
const professionals = ref([]);

const patient = computed(() => props.patientData || {});

// Carrega lista de profissionais
const fetchProfessionals = async () => {
  try {
    const res = await schedulingAPI.getProfessionals();
    professionals.value = Array.isArray(res.data) ? res.data : (res.data?.professionals || []);
  } catch (err) {
    console.error('Erro ao carregar profissionais:', err);
  }
};

onMounted(() => {
  fetchProfessionals();
});

// Inicializa prontuario ao carregar
watch(
  () => props.patientData,
  (newVal) => {
    if (newVal?.medical_record !== undefined) {
      localMedicalRecord.value = newVal.medical_record || '';
    }
  },
  { immediate: true }
);

// Idade calculada
const patientAge = computed(() => {
  if (!patient.value.birth_date) return null;
  const birth = new Date(patient.value.birth_date);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
});

// Histórico de atendimentos filtrado
const filteredHistory = computed(() => {
  const history = patient.value.appointments_history || [];
  if (timelineFilter.value === 'all') return history;
  if (timelineFilter.value === 'completed') return history.filter(h => h.status === 'completed');
  if (timelineFilter.value === 'cancelled') return history.filter(h => h.status === 'cancelled');
  if (timelineFilter.value === 'future') return history.filter(h => h.is_future && h.status === 'confirmed');
  return history;
});

const saveProntuario = async () => {
  if (!patient.value.id) return;
  isSavingProntuario.value = true;
  try {
    await schedulingAPI.updatePatient(patient.value.id, {
      medical_record: localMedicalRecord.value,
    });
    emit('updated');
  } catch (err) {
    console.error('Erro ao salvar prontuário:', err);
    alert('Erro ao salvar prontuário.');
  } finally {
    isSavingProntuario.value = false;
  }
};

const handleAddSecondaryContact = async () => {
  if (!newContactPhone.value.trim()) return;
  isAddingContact.value = true;
  try {
    await schedulingAPI.addSecondaryContact(patient.value.id, {
      phone: newContactPhone.value.trim(),
      name: newContactName.value.trim() || newContactPhone.value.trim(),
      relationship: newContactRel.value,
    });
    newContactPhone.value = '';
    newContactName.value = '';
    emit('updated');
  } catch (err) {
    console.error('Erro ao adicionar contato secundário:', err);
    alert('Erro ao vincular contato.');
  } finally {
    isAddingContact.value = false;
  }
};

const handleRemoveSecondaryContact = async (phone) => {
  if (!confirm('Deseja realmente remover este telefone secundário?')) return;
  try {
    await schedulingAPI.removeSecondaryContact(patient.value.id, phone);
    emit('updated');
  } catch (err) {
    console.error('Erro ao remover contato secundário:', err);
  }
};

// ==========================================
// AÇÕES RÁPIDAS DE ATENDIMENTO (PRESENTE / FALTA / PRONTUÁRIO)
// ==========================================
const isResolvingAttendance = ref(false);
const showEvolutionModal = ref(false);
const activeItemForEvolution = ref(null);
const sessionEvolutionNotes = ref('');

const handleMarkPresent = async (item) => {
  isResolvingAttendance.value = true;
  try {
    if (item.type === 'attendance') {
      await schedulingAPI.updateAttendance(item.id, { prontuario_notes: item.prontuario_notes });
    } else {
      await schedulingAPI.updateAppointmentStatus(item.id, 'completed');
    }
    emit('updated');
  } catch (err) {
    console.error('Erro ao marcar presença:', err);
    alert('Erro ao marcar presença.');
  } finally {
    isResolvingAttendance.value = false;
  }
};

const handleMarkAbsent = async (item) => {
  if (!confirm(`Registrar falta/cancelamento do atendimento de ${item.date} às ${item.time_slot}?`)) return;
  isResolvingAttendance.value = true;
  try {
    await schedulingAPI.updateAppointmentStatus(item.id, 'cancelled');
    emit('updated');
  } catch (err) {
    console.error('Erro ao registrar falta:', err);
    alert('Erro ao registrar falta.');
  } finally {
    isResolvingAttendance.value = false;
  }
};

const openAddEvolution = (item) => {
  activeItemForEvolution.value = item;
  sessionEvolutionNotes.value = item.prontuario_notes || '';
  showEvolutionModal.value = true;
};

const saveSessionEvolution = async () => {
  if (!activeItemForEvolution.value) return;
  isResolvingAttendance.value = true;
  try {
    if (activeItemForEvolution.value.type === 'attendance') {
      await schedulingAPI.updateAttendance(activeItemForEvolution.value.id, {
        prontuario_notes: sessionEvolutionNotes.value.trim(),
      });
    } else {
      await schedulingAPI.updateAppointmentProntuario(
        activeItemForEvolution.value.id,
        sessionEvolutionNotes.value.trim()
      );
    }
    showEvolutionModal.value = false;
    emit('updated');
  } catch (err) {
    console.error('Erro ao salvar evolução da sessão:', err);
    alert('Erro ao salvar evolução clínica.');
  } finally {
    isResolvingAttendance.value = false;
  }
};

// ==========================================
// REAGENDAMENTO INTELIGENTE (HEATMAP & MINI-CALENDÁRIO)
// ==========================================
const showRescheduleModal = ref(false);
const selectedItemForReschedule = ref(null);
const calYear = ref(new Date().getFullYear());
const calMonth = ref(new Date().getMonth() + 1);
const monthOverviewData = ref({ days: {}, day_slots: {} });
const isLoadingMonthOverview = ref(false);
const isRescheduling = ref(false);

const rescheduleForm = ref({
  targetDate: '',
  targetTime: '08:00',
  professionalId: null,
});

const openReschedule = (item) => {
  selectedItemForReschedule.value = item;
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  calYear.value = today.getFullYear();
  calMonth.value = today.getMonth() + 1;
  rescheduleForm.value = {
    targetDate: `${y}-${m}-${d}`,
    targetTime: item.time_slot || '08:00',
    professionalId: item.professional_id || professionals.value[0]?.id || null,
  };
  showRescheduleModal.value = true;
  loadMonthOverview();
};

const loadMonthOverview = async () => {
  if (!rescheduleForm.value.professionalId) return;
  isLoadingMonthOverview.value = true;
  try {
    const res = await schedulingAPI.getMonthOverview({
      professional_id: rescheduleForm.value.professionalId,
      year: calYear.value,
      month: calMonth.value,
    });
    monthOverviewData.value = res.data || { days: {}, day_slots: {} };
  } catch (err) {
    console.error('Erro ao carregar mapa de calor de vagas:', err);
  } finally {
    isLoadingMonthOverview.value = false;
  }
};

const currentMonthLabel = computed(() => {
  const date = new Date(calYear.value, calMonth.value - 1, 1);
  return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
});

const calendarDaysGrid = computed(() => {
  const y = calYear.value;
  const m = calMonth.value;
  const firstDay = new Date(y, m - 1, 1);
  const lastDay = new Date(y, m, 0);
  const totalDays = lastDay.getDate();

  let startDayOfWeek = firstDay.getDay() - 1;
  if (startDayOfWeek === -1) startDayOfWeek = 6;

  const grid = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    grid.push({ isBlank: true });
  }

  const todayStr = new Date().toISOString().split('T')[0];
  const overviewDays = monthOverviewData.value?.days || {};

  for (let day = 1; day <= totalDays; day++) {
    const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayInfo = overviewDays[dateStr] || {
      status: 'off',
      total_slots: 0,
      available_slots: 0,
    };

    const isPast = dateStr < todayStr;
    const isSelected = rescheduleForm.value.targetDate === dateStr;
    const isOff = dayInfo.status === 'off';
    const isBlocked = dayInfo.status === 'blocked';
    const isFull = dayInfo.status === 'full';
    const isAvailable = dayInfo.status === 'available' || dayInfo.status === 'partial';

    grid.push({
      dateStr,
      dayNum: day,
      isBlank: false,
      isPast,
      isSelected,
      isOff,
      isBlocked,
      isFull,
      status: dayInfo.status,
      availableSlots: dayInfo.available_slots || 0,
      isClickable: !isPast && !isOff && isAvailable,
    });
  }

  return grid;
});

const selectedDaySlots = computed(() => {
  if (!rescheduleForm.value.targetDate) return [];
  const slotsMap = monthOverviewData.value?.day_slots || {};
  return slotsMap[rescheduleForm.value.targetDate] || [];
});

const onSelectCalendarDay = (cell) => {
  if (!cell.isClickable) return;
  rescheduleForm.value.targetDate = cell.dateStr;
  const slots = monthOverviewData.value?.day_slots?.[cell.dateStr] || [];
  const firstFree = slots.find(s => s.available);
  if (firstFree) {
    rescheduleForm.value.targetTime = firstFree.time_label;
  }
};

const prevMonth = () => {
  if (calMonth.value === 1) {
    calMonth.value = 12;
    calYear.value--;
  } else {
    calMonth.value--;
  }
  loadMonthOverview();
};

const nextMonth = () => {
  if (calMonth.value === 12) {
    calMonth.value = 1;
    calYear.value++;
  } else {
    calMonth.value++;
  }
  loadMonthOverview();
};

const confirmReschedule = async () => {
  if (!selectedItemForReschedule.value?.id) return;
  isRescheduling.value = true;
  try {
    const startDateTime = `${rescheduleForm.value.targetDate}T${rescheduleForm.value.targetTime}:00`;
    const payload = {
      start_time: startDateTime,
      status: 'confirmed',
    };
    if (rescheduleForm.value.professionalId) {
      payload.scheduling_professional_id = rescheduleForm.value.professionalId;
    }
    await schedulingAPI.updateAppointment(selectedItemForReschedule.value.id, payload);
    showRescheduleModal.value = false;
    emit('updated');
  } catch (err) {
    console.error('Erro ao reagendar atendimento:', err);
    alert('Erro ao reagendar atendimento.');
  } finally {
    isRescheduling.value = false;
  }
};
</script>

<template>
  <div
    v-if="show && patient.id"
    class="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-slate-950/80 dark:bg-slate-950/85 backdrop-blur-xs animate-fade-in"
    @click.self="emit('close')"
  >
    <div class="w-full max-w-6xl xl:max-w-7xl h-[94vh] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden text-slate-800 dark:text-slate-100 flex flex-col">
      <!-- Header 360 do Paciente -->
      <div class="px-8 py-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/90 flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0">
        <!-- Lado Esquerdo: Avatar, Nome e Badges -->
        <div class="flex items-center gap-4">
          <div
            class="w-14 h-14 rounded-2xl font-black flex items-center justify-center text-lg shrink-0 shadow-inner"
            :class="patient.is_blocked ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30' : 'bg-blue-100 dark:bg-blue-600/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30'"
          >
            {{ (patient.name || 'P').charAt(0).toUpperCase() }}
          </div>
          <div>
            <div class="flex items-center gap-3 flex-wrap">
              <h2 class="text-xl font-black text-slate-900 dark:text-white leading-tight">
                {{ patient.name }}
              </h2>

              <!-- Status Clínico Badge -->
              <span
                v-if="patient.is_blocked"
                class="px-3 py-1 rounded-full text-xs font-bold bg-rose-50 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30"
              >
                🔴 Bloqueado
              </span>
              <span
                v-else-if="patient.discharged_at"
                class="px-3 py-1 rounded-full text-xs font-bold bg-cyan-50 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-500/30"
              >
                🔵 Alta Clínica
              </span>
              <span
                v-else-if="patient.computed_status === 'active'"
                class="px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30"
              >
                🟢 Ativo
              </span>
              <span
                v-else
                class="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-400 border border-slate-200 dark:border-slate-700"
              >
                🟡 Inativo
              </span>
            </div>

            <!-- Dados Rápidos de Identificação -->
            <div class="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400 mt-1.5 flex-wrap font-medium">
              <span v-if="patient.phone_number" class="font-mono text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded-md border border-slate-200 dark:border-slate-800">
                📱 {{ patient.phone_number }}
              </span>
              <span v-if="patient.email" class="text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded-md border border-slate-200 dark:border-slate-800">
                ✉️ {{ patient.email }}
              </span>
              <span v-if="patient.document_number" class="font-mono text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded-md border border-slate-200 dark:border-slate-800">
                CPF: {{ patient.document_number }}
              </span>
              <span v-if="patient.birth_date" class="text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded-md border border-slate-200 dark:border-slate-800">
                🎂 {{ patient.birth_date }} {{ patientAge !== null ? `(${patientAge} anos)` : '' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Lado Direito: Ações Rápidas & Fechar -->
        <div class="flex items-center gap-2 self-start md:self-center">
          <button
            type="button"
            class="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white text-xs font-semibold border border-slate-200 dark:border-slate-700 transition-all cursor-pointer shadow-xs"
            @click="emit('edit-patient', patient)"
          >
            ✏️ Editar
          </button>
          <button
            type="button"
            class="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-800/60 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white flex items-center justify-center cursor-pointer transition-all text-sm"
            @click="emit('close')"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Barra de KPIs / Métricas Clínicas -->
      <div class="grid grid-cols-2 sm:grid-cols-5 gap-2 px-8 py-3.5 bg-slate-50/70 dark:bg-slate-950/60 border-b border-slate-200 dark:border-slate-800 text-center">
        <div class="p-2 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800/80 shadow-xs">
          <span class="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block">Presenças</span>
          <span class="text-base font-black text-emerald-600 dark:text-emerald-400 font-mono">{{ patient.stats?.completed_sessions || patient.completed_sessions_count || 0 }}</span>
        </div>
        <div class="p-2 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800/80 shadow-xs">
          <span class="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block">Saldo Restante</span>
          <span class="text-base font-black text-blue-600 dark:text-blue-400 font-mono">{{ patient.remaining_sessions || patient.remaining_sessions_count || 0 }}</span>
        </div>
        <div class="p-2 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800/80 shadow-xs">
          <span class="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block">Frequência</span>
          <span class="text-base font-black text-slate-900 dark:text-white font-mono">{{ (patient.weekly_frequency || patient.stats?.active_enrollments || patient.active_enrollments_count || 0) ? (patient.weekly_frequency || patient.stats?.active_enrollments || patient.active_enrollments_count) + 'x/sem' : 'Avulso' }}</span>
        </div>
        <div class="p-2 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800/80 shadow-xs">
          <span class="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block">Faltas / Canc.</span>
          <span class="text-base font-black text-rose-600 dark:text-rose-400 font-mono">{{ patient.stats?.cancelled_sessions || 0 }}</span>
        </div>
        <div class="p-2 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800/80 shadow-xs col-span-2 sm:col-span-1">
          <span class="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block">Agendados</span>
          <span class="text-base font-black text-amber-600 dark:text-amber-300 font-mono">{{ patient.stats?.future_sessions || 0 }}</span>
        </div>
      </div>

      <!-- Navegação de Abas -->
      <div class="flex items-center gap-2 px-8 pt-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs font-bold">
        <button
          type="button"
          class="pb-2.5 px-3 border-b-2 transition-all cursor-pointer flex items-center gap-1.5"
          :class="activeTab === 'timeline' ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
          @click="activeTab = 'timeline'"
        >
          <span>📑</span> Linha do Tempo & Atendimentos
          <span class="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">{{ patient.appointments_history?.length || 0 }}</span>
        </button>

        <button
          type="button"
          class="pb-2.5 px-3 border-b-2 transition-all cursor-pointer flex items-center gap-1.5"
          :class="activeTab === 'prontuario' ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
          @click="activeTab = 'prontuario'"
        >
          <span>📋</span> Prontuário Geral
        </button>

        <button
          type="button"
          class="pb-2.5 px-3 border-b-2 transition-all cursor-pointer flex items-center gap-1.5"
          :class="activeTab === 'enrollments' ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
          @click="activeTab = 'enrollments'"
        >
          <span>🗓️</span> Matrículas Fixas
          <span class="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">{{ patient.enrollments?.length || 0 }}</span>
        </button>

        <button
          type="button"
          class="pb-2.5 px-3 border-b-2 transition-all cursor-pointer flex items-center gap-1.5"
          :class="activeTab === 'contacts' ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
          @click="activeTab = 'contacts'"
        >
          <span>👥</span> Contatos Familiares
          <span class="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">{{ patient.secondary_contacts?.length || 0 }}</span>
        </button>
      </div>

      <!-- Conteúdo da Aba Ativa -->
      <div class="px-8 py-6 overflow-y-auto flex-1 space-y-6">
        <!-- 📑 ABA 1: Linha do Tempo de Atendimentos -->
        <div v-if="activeTab === 'timeline'" class="space-y-4">
          <!-- Filtros da Linha do Tempo -->
          <div class="flex items-center justify-between gap-3 flex-wrap pb-2 border-b border-slate-200 dark:border-slate-800/60">
            <div class="flex items-center gap-2 text-xs">
              <button
                type="button"
                class="px-3 py-1.5 rounded-xl font-bold transition-all cursor-pointer"
                :class="timelineFilter === 'all' ? 'bg-slate-200 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-300 dark:border-slate-700 shadow-xs' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'"
                @click="timelineFilter = 'all'"
              >
                Todos ({{ patient.appointments_history?.length || 0 }})
              </button>
              <button
                type="button"
                class="px-3 py-1.5 rounded-xl font-bold transition-all cursor-pointer"
                :class="timelineFilter === 'completed' ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/40 shadow-xs' : 'text-slate-500 dark:text-slate-400 hover:text-emerald-700 dark:hover:text-emerald-300'"
                @click="timelineFilter = 'completed'"
              >
                ✓ Presenças ({{ patient.appointments_history?.filter(h => h.status === 'completed').length || 0 }})
              </button>
              <button
                type="button"
                class="px-3 py-1.5 rounded-xl font-bold transition-all cursor-pointer"
                :class="timelineFilter === 'future' ? 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/40 shadow-xs' : 'text-slate-500 dark:text-slate-400 hover:text-blue-700 dark:hover:text-blue-300'"
                @click="timelineFilter = 'future'"
              >
                ⏰ Futuros ({{ patient.appointments_history?.filter(h => h.is_future && h.status === 'confirmed').length || 0 }})
              </button>
              <button
                type="button"
                class="px-3 py-1.5 rounded-xl font-bold transition-all cursor-pointer"
                :class="timelineFilter === 'cancelled' ? 'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/40 shadow-xs' : 'text-slate-500 dark:text-slate-400 hover:text-rose-700 dark:hover:text-rose-300'"
                @click="timelineFilter = 'cancelled'"
              >
                ✕ Faltas / Canc. ({{ patient.appointments_history?.filter(h => h.status === 'cancelled').length || 0 }})
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="filteredHistory.length === 0" class="py-16 text-center text-xs text-slate-500 bg-slate-50/50 dark:bg-slate-950/40 rounded-2xl border border-slate-200 dark:border-slate-800">
            Nenhum atendimento registrado neste filtro.
          </div>

          <!-- Cards de Histórico / Sessões com AÇÕES RÁPIDAS -->
          <div v-else class="space-y-3.5">
            <div
              v-for="item in filteredHistory"
              :key="`${item.type}-${item.id}`"
              class="p-5 rounded-2xl bg-slate-50/80 dark:bg-slate-950/70 border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700/80 transition-all space-y-3 shadow-xs"
            >
              <div class="flex items-center justify-between flex-wrap gap-2">
                <div class="flex items-center gap-2.5">
                  <span
                    class="text-xs px-3 py-1 rounded-full font-bold bg-white dark:bg-slate-900 border shadow-xs"
                    :style="{
                      borderColor: item.service_color ? `${item.service_color}60` : '#3b82f660',
                      color: item.service_color || '#3b82f6'
                    }"
                  >
                    {{ item.service_name }}
                  </span>

                  <!-- Badge de Status da Sessão -->
                  <span
                    v-if="item.status === 'completed'"
                    class="px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30"
                  >
                    ✓ Presente
                  </span>
                  <span
                    v-else-if="item.status === 'cancelled'"
                    class="px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-rose-50 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30"
                  >
                    ✕ Falta / Cancelado
                  </span>
                  <span
                    v-else-if="item.is_future"
                    class="px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30"
                  >
                    ⏰ Agendado
                  </span>
                  <span
                    v-else
                    class="px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30"
                  >
                    ⚠️ Pendente
                  </span>
                </div>

                <!-- LADO DIREITO: DATA E AÇÕES RÁPIDAS (PRESENTE, REAGENDAR, FALTA) -->
                <div class="flex items-center gap-3">
                  <!-- Botões de Ação para Atendimentos em Aberto / Pendentes / Futuros -->
                  <div
                    v-if="item.status !== 'completed' && item.status !== 'cancelled'"
                    class="flex items-center gap-2"
                  >
                    <button
                      type="button"
                      class="px-3 py-1 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-600 hover:text-white border border-emerald-200 dark:border-emerald-500/40 font-bold text-xs transition-all cursor-pointer flex items-center gap-1 shadow-xs"
                      title="Confirmar Presença do Paciente"
                      :disabled="isResolvingAttendance"
                      @click="handleMarkPresent(item)"
                    >
                      ✓ Presente
                    </button>

                    <button
                      type="button"
                      class="px-3 py-1 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 hover:bg-blue-600 hover:text-white border border-blue-200 dark:border-blue-500/40 font-bold text-xs transition-all cursor-pointer flex items-center gap-1 shadow-xs"
                      title="Reagendar horário e data no calendário"
                      @click="openReschedule(item)"
                    >
                      🔄 Reagendar
                    </button>

                    <button
                      type="button"
                      class="px-3 py-1 rounded-xl bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 hover:bg-rose-600 hover:text-white border border-rose-200 dark:border-rose-500/40 font-bold text-xs transition-all cursor-pointer flex items-center gap-1 shadow-xs"
                      title="Registrar Falta / Cancelamento"
                      :disabled="isResolvingAttendance"
                      @click="handleMarkAbsent(item)"
                    >
                      ✕ Falta
                    </button>
                  </div>

                  <!-- Botão de Anotar/Editar Evolução se já foi Concluído -->
                  <button
                    v-else-if="item.status === 'completed'"
                    type="button"
                    class="px-2.5 py-1 rounded-lg bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-slate-700 text-xs font-semibold transition-all cursor-pointer flex items-center gap-1 shadow-xs"
                    @click="openAddEvolution(item)"
                  >
                    ✏️ {{ item.prontuario_notes ? 'Editar Evolução' : '+ Anotar Evolução' }}
                  </button>

                  <div class="text-sm font-mono text-slate-800 dark:text-slate-200 font-bold bg-white dark:bg-slate-900/90 px-3 py-1 rounded-xl border border-slate-200 dark:border-slate-800">
                    📅 {{ item.date }} às {{ item.time_slot }}
                  </div>
                </div>
              </div>

              <div class="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-4 flex-wrap">
                <span>👤 Profissional: <strong class="text-slate-800 dark:text-slate-200 font-semibold">{{ item.professional_name }}</strong></span>
                <span v-if="item.session_number">
                  • Sessão: <strong class="text-blue-600 dark:text-blue-400 font-mono font-bold">{{ item.session_number }}{{ item.total_sessions ? `/${item.total_sessions}` : '' }}</strong>
                </span>
                <span>• Tipo: <strong class="text-slate-700 dark:text-slate-300">{{ item.type === 'attendance' ? 'Turma Fixa' : 'Consulta / Avulso' }}</strong></span>
              </div>

              <!-- Evolução Clínica / Prontuário Anotado -->
              <div v-if="item.prontuario_notes" class="pt-2.5 border-t border-slate-200 dark:border-slate-800/80">
                <span class="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">📝 Evolução Clínica da Sessão:</span>
                <p class="text-xs text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900/90 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 whitespace-pre-wrap leading-relaxed">
                  {{ item.prontuario_notes }}
                </p>
              </div>
              <div v-else class="text-[11px] text-slate-400 dark:text-slate-500 italic pt-1 border-t border-slate-200 dark:border-slate-800/40 flex items-center justify-between">
                <span>Sem anotações de evolução clínica registradas para este horário.</span>
                <button
                  type="button"
                  class="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-xs font-semibold underline cursor-pointer"
                  @click="openAddEvolution(item)"
                >
                  + Adicionar evolução clínica
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 📋 ABA 2: Prontuário Clínico Geral -->
        <div v-if="activeTab === 'prontuario'" class="space-y-4 max-w-4xl">
          <!-- Alerta se estiver com Alta Clínica -->
          <div v-if="patient.discharged_at" class="p-4 rounded-2xl bg-cyan-50 dark:bg-cyan-950/40 border border-cyan-200 dark:border-cyan-500/40 space-y-1.5">
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-cyan-800 dark:text-cyan-300 flex items-center gap-1.5">
                <span>🔵</span>
                <span>Alta Clínica Concedida em {{ new Date(patient.discharged_at).toLocaleDateString('pt-BR') }}</span>
              </span>
              <button
                type="button"
                class="text-xs font-semibold text-cyan-700 dark:text-cyan-300 underline cursor-pointer hover:text-cyan-900 dark:hover:text-white"
                @click="emit('reopen-treatment', patient)"
              >
                Reabrir Tratamento
              </button>
            </div>
            <p v-if="patient.discharged_notes" class="text-xs text-slate-700 dark:text-slate-200">
              {{ patient.discharged_notes }}
            </p>
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-2">
              Prontuário Geral, Anamnese & Anotações Permanentes
            </label>
            <textarea
              v-model="localMedicalRecord"
              rows="12"
              placeholder="Descreva o histórico de saúde do paciente, queixas principais, diagnósticos prévios, alergias, medicações de uso contínuo, objetivos do tratamento e recomendações gerais..."
              class="w-full p-4 text-xs sm:text-sm bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-2xl focus:ring-1 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white leading-relaxed resize-none"
            />
          </div>

          <div class="flex items-center justify-end">
            <button
              type="button"
              class="px-6 py-2.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl transition-all cursor-pointer shadow-md flex items-center gap-1.5 disabled:opacity-50"
              :disabled="isSavingProntuario"
              @click="saveProntuario"
            >
              <span>{{ isSavingProntuario ? 'Salvando...' : '✓ Salvar Prontuário Geral' }}</span>
            </button>
          </div>
        </div>

        <!-- 🗓️ ABA 3: Matrículas e Turmas Fixas -->
        <div v-if="activeTab === 'enrollments'" class="space-y-4">
          <div v-if="!patient.enrollments || patient.enrollments.length === 0" class="py-16 text-center text-xs text-slate-500 bg-slate-50/50 dark:bg-slate-950/40 rounded-2xl border border-slate-200 dark:border-slate-800">
            O paciente não possui nenhuma matrícula fixa na grade no momento.
          </div>

          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="enr in patient.enrollments"
              :key="enr.id"
              class="p-5 rounded-2xl bg-slate-50/80 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 space-y-3.5 shadow-xs"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-xs font-bold px-3 py-1 rounded-full bg-white dark:bg-slate-900 border shadow-xs"
                  :style="{
                    borderColor: enr.service_color ? `${enr.service_color}60` : '#3b82f660',
                    color: enr.service_color || '#3b82f6'
                  }"
                >
                  {{ enr.service_name }}
                </span>
                <span
                  class="text-[10px] font-bold px-2.5 py-0.5 rounded-md"
                  :class="enr.status === 'active' ? 'bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-400 border border-slate-200 dark:border-slate-700'"
                >
                  {{ enr.status === 'active' ? 'Matrícula Ativa' : 'Encerrada' }}
                </span>
              </div>

              <div class="text-xs space-y-1.5">
                <div class="font-bold text-slate-900 dark:text-white text-sm flex items-center gap-2">
                  <span>📅 {{ enr.day_of_week_label || enr.day_of_week }} às {{ enr.time_slot }}</span>
                </div>
                <div class="text-slate-500 dark:text-slate-400">
                  Profissional: <strong class="text-slate-700 dark:text-slate-300">{{ enr.professional_name || 'Geral' }}</strong>
                </div>
              </div>

              <!-- Progresso de Sessões -->
              <div class="pt-3 border-t border-slate-200 dark:border-slate-800/80 space-y-1.5">
                <div class="flex items-center justify-between text-xs">
                  <span class="text-slate-500 dark:text-slate-400">Ciclo de Sessões:</span>
                  <span class="font-bold font-mono text-blue-600 dark:text-blue-400">{{ enr.completed_sessions || 0 }} de {{ enr.total_sessions || 0 }}</span>
                </div>
                <div class="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                  <div
                    class="h-full bg-blue-500 rounded-full transition-all"
                    :style="{ width: `${Math.min(100, Math.round(((enr.completed_sessions || 0) / (enr.total_sessions || 1)) * 100))}%` }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 👥 ABA 4: Contatos Familiares -->
        <div v-if="activeTab === 'contacts'" class="space-y-4">
          <!-- Formulário para adicionar contato secundário -->
          <div class="p-4 rounded-2xl bg-slate-50/80 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 space-y-3">
            <span class="text-xs font-bold text-slate-900 dark:text-white block">➕ Vincular Novo Contato Familiar / Responsável</span>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <input
                v-model="newContactPhone"
                type="text"
                placeholder="Telefone / WhatsApp"
                class="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs text-slate-900 dark:text-white rounded-xl p-2 focus:ring-1 focus:ring-blue-500 outline-none"
              />
              <input
                v-model="newContactName"
                type="text"
                placeholder="Nome do Responsável"
                class="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs text-slate-900 dark:text-white rounded-xl p-2 focus:ring-1 focus:ring-blue-500 outline-none"
              />
              <select
                v-model="newContactRel"
                class="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs text-slate-900 dark:text-white rounded-xl p-2 focus:ring-1 focus:ring-blue-500 outline-none"
              >
                <option value="Familiar / Responsável">Familiar / Responsável</option>
                <option value="Mãe / Pai">Mãe / Pai</option>
                <option value="Cônjuge">Cônjuge</option>
                <option value="Filho(a)">Filho(a)</option>
              </select>
            </div>
            <div class="flex justify-end">
              <button
                type="button"
                class="px-4 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl transition-all cursor-pointer shadow-xs disabled:opacity-50"
                :disabled="isAddingContact || !newContactPhone.trim()"
                @click="handleAddSecondaryContact"
              >
                {{ isAddingContact ? 'Vinculando...' : 'Adicionar Contato' }}
              </button>
            </div>
          </div>

          <!-- Lista de Contatos Secundários -->
          <div v-if="!patient.secondary_contacts || patient.secondary_contacts.length === 0" class="py-8 text-center text-xs text-slate-500">
            Nenhum contato familiar adicional vinculado.
          </div>
          <div v-else class="space-y-2">
            <div
              v-for="sc in patient.secondary_contacts"
              :key="sc.phone"
              class="p-3 rounded-xl bg-white dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 flex items-center justify-between"
            >
              <div>
                <span class="font-bold text-slate-900 dark:text-white text-xs block">{{ sc.name || sc.phone }}</span>
                <span class="text-[11px] text-slate-500 dark:text-slate-400 font-mono">{{ sc.phone }} ({{ sc.relationship || 'Familiar' }})</span>
              </div>
              <button
                type="button"
                class="text-xs text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 p-1 cursor-pointer font-semibold"
                title="Remover contato"
                @click="handleRemoveSecondaryContact(sc.phone)"
              >
                ✕ Remover
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer com Ações Clínicas -->
      <div class="px-8 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400 shrink-0">
        <div class="flex items-center gap-2">
          <!-- Ação Alta Clínica / Reabrir -->
          <button
            v-if="!patient.discharged_at"
            type="button"
            class="px-3.5 py-1.5 rounded-xl bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-600 hover:text-white border border-cyan-200 dark:border-cyan-500/40 transition-all cursor-pointer font-semibold"
            @click="emit('discharge-patient', patient)"
          >
            🔵 Conceder Alta
          </button>
          <button
            v-else
            type="button"
            class="px-3.5 py-1.5 rounded-xl bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-600 hover:text-white border border-cyan-200 dark:border-cyan-500/40 transition-all cursor-pointer font-semibold"
            @click="emit('reopen-treatment', patient)"
          >
            🔄 Reabrir Tratamento
          </button>

          <!-- Ação Bloquear / Desbloquear -->
          <button
            v-if="!patient.is_blocked"
            type="button"
            class="px-3.5 py-1.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 hover:bg-rose-600 hover:text-white border border-rose-200 dark:border-rose-500/40 transition-all cursor-pointer font-semibold"
            @click="emit('block-patient', patient)"
          >
            🚫 Bloquear
          </button>
          <button
            v-else
            type="button"
            class="px-3.5 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-600 hover:text-white border border-emerald-200 dark:border-emerald-500/40 transition-all cursor-pointer font-semibold"
            @click="emit('unblock-patient', patient)"
          >
            ✓ Desbloquear
          </button>
        </div>

        <button
          type="button"
          class="px-6 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white cursor-pointer bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 rounded-xl transition-all shadow-xs"
          @click="emit('close')"
        >
          Fechar
        </button>
      </div>
    </div>

    <!-- ======================================================== -->
    <!-- MODAL DE EVOLUÇÃO CLÍNICA / PRONTUÁRIO DA SESSÃO         -->
    <!-- ======================================================== -->
    <div
      v-if="showEvolutionModal && activeItemForEvolution"
      class="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
      @click.self="showEvolutionModal = false"
    >
      <div class="w-full max-w-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden text-slate-800 dark:text-slate-100 flex flex-col space-y-4 p-6">
        <div class="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
          <div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span>📝</span>
              <span>Evolução Clínica: {{ patient.name }}</span>
            </h3>
            <span class="text-xs text-slate-500 dark:text-slate-400">
              Sessão de {{ activeItemForEvolution.date }} às {{ activeItemForEvolution.time_slot }} ({{ activeItemForEvolution.service_name }})
            </span>
          </div>
          <button type="button" class="text-slate-400 hover:text-slate-600 dark:hover:text-white" @click="showEvolutionModal = false">✕</button>
        </div>

        <div>
          <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
            Anotações do Atendimento / Conduta Clínica
          </label>
          <textarea
            v-model="sessionEvolutionNotes"
            rows="5"
            placeholder="Descreva a evolução do paciente, exercícios realizados, queixas no início e final da sessão, orientações passadas..."
            class="w-full p-3 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl focus:ring-1 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white leading-relaxed resize-none"
          />
        </div>

        <div class="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            class="px-4 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800 rounded-xl cursor-pointer"
            @click="showEvolutionModal = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl transition-all cursor-pointer shadow-md disabled:opacity-50"
            :disabled="isResolvingAttendance"
            @click="saveSessionEvolution"
          >
            {{ isResolvingAttendance ? 'Salvando...' : 'Salvar Evolução' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ======================================================== -->
    <!-- MODAL DE REAGENDAMENTO COM MINI-CALENDÁRIO HEATMAP       -->
    <!-- ======================================================== -->
    <div
      v-if="showRescheduleModal && selectedItemForReschedule"
      class="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
      @click.self="showRescheduleModal = false"
    >
      <div class="w-full max-w-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden text-slate-800 dark:text-slate-100 flex flex-col max-h-[90vh]">
        <!-- Header do Reagendamento -->
        <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/90 flex items-center justify-between">
          <div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span>🔄</span>
              <span>Reagendar Atendimento: {{ patient.name }}</span>
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              Atendimento original: <strong class="text-slate-800 dark:text-slate-300">{{ selectedItemForReschedule.date }} às {{ selectedItemForReschedule.time_slot }}</strong>
            </p>
          </div>
          <button type="button" class="text-slate-400 hover:text-slate-600 dark:hover:text-white text-sm cursor-pointer" @click="showRescheduleModal = false">✕</button>
        </div>

        <!-- Seletor de Profissional e Legenda -->
        <div class="px-6 py-3 bg-slate-100/70 dark:bg-slate-950/60 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <span class="text-xs font-bold text-slate-700 dark:text-slate-300">Profissional:</span>
            <select
              v-model="rescheduleForm.professionalId"
              class="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs font-semibold text-slate-900 dark:text-white rounded-xl px-3 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none"
              @change="loadMonthOverview"
            >
              <option v-for="prof in professionals" :key="prof.id" :value="prof.id">
                {{ prof.name }} (Cap. {{ prof.max_concurrent_per_slot || 1 }})
              </option>
            </select>
          </div>

          <!-- Legenda de Cores -->
          <div class="flex items-center gap-4 text-xs font-semibold">
            <span class="text-slate-700 dark:text-slate-100 flex items-center gap-1.5"><span class="w-2 h-2 rounded-full bg-slate-400 dark:bg-slate-300"></span> Disponível</span>
            <span class="font-bold flex items-center gap-1.5 text-amber-600 dark:text-amber-400"><span class="w-2 h-2 rounded-full bg-amber-500"></span> Vagas parciais</span>
            <span class="font-bold line-through flex items-center gap-1.5 text-rose-600 dark:text-rose-400"><span class="w-2 h-2 rounded-full bg-rose-500"></span> Lotado / Bloqueado</span>
          </div>
        </div>

        <!-- Conteúdo em 2 Colunas -->
        <div class="p-6 overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-12 gap-6">
          <!-- Coluna 1: Mini Calendário -->
          <div class="md:col-span-6 bg-slate-50 dark:bg-slate-950/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
            <div class="flex items-center justify-between px-1">
              <span class="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200">
                {{ currentMonthLabel }}
              </span>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="w-7 h-7 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-slate-700 flex items-center justify-center text-xs transition-all cursor-pointer"
                  @click="prevMonth"
                >
                  ◀
                </button>
                <button
                  type="button"
                  class="w-7 h-7 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-slate-700 flex items-center justify-center text-xs transition-all cursor-pointer"
                  @click="nextMonth"
                >
                  ▶
                </button>
              </div>
            </div>

            <!-- Dias da Semana Header -->
            <div class="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase py-1 border-b border-slate-200 dark:border-slate-800/60">
              <span>Seg</span>
              <span>Ter</span>
              <span>Qua</span>
              <span>Qui</span>
              <span>Sex</span>
              <span>Sáb</span>
              <span>Dom</span>
            </div>

            <!-- Loading Spinner -->
            <div v-if="isLoadingMonthOverview" class="py-14 flex justify-center items-center">
              <div class="animate-spin rounded-full h-7 w-7 border-2 border-blue-500 border-t-transparent"></div>
            </div>

            <!-- Grid de Dias -->
            <div v-else class="grid grid-cols-7 gap-1.5">
              <template v-for="(cell, cIdx) in calendarDaysGrid" :key="cIdx">
                <div v-if="cell.isBlank" class="h-9"></div>
                <button
                  v-else
                  type="button"
                  class="h-9 rounded-xl text-xs flex items-center justify-center transition-all select-none"
                  :class="[
                    cell.isSelected
                      ? 'bg-blue-600 text-white font-black shadow-md ring-2 ring-blue-400 scale-105'
                      : cell.isPast || cell.isOff
                        ? 'text-slate-400 dark:text-slate-600 opacity-30 cursor-not-allowed bg-transparent'
                        : cell.isBlocked || cell.isFull
                          ? 'font-bold opacity-80 line-through bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-900/30 cursor-not-allowed'
                          : cell.status === 'partial'
                            ? 'font-bold bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30 hover:border-amber-400 hover:bg-amber-100 dark:hover:bg-amber-950/40 cursor-pointer'
                            : 'text-slate-800 dark:text-slate-100 font-semibold bg-white dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/80 cursor-pointer'
                  ]"
                  :disabled="!cell.isClickable"
                  :title="cell.isPast ? 'Data passada' : cell.isOff ? 'Sem atendimento' : cell.isFull ? 'Dia lotado' : cell.status === 'partial' ? `Vagas parciais (${cell.availableSlots} livres)` : 'Dia disponível'"
                  @click="onSelectCalendarDay(cell)"
                >
                  {{ cell.dayNum }}
                </button>
              </template>
            </div>
          </div>

          <!-- Coluna 2: Horários do Dia Selecionado -->
          <div class="md:col-span-6 bg-slate-50 dark:bg-slate-950/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3 flex flex-col justify-between">
            <div class="space-y-3">
              <div class="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-slate-800/60">
                <span class="text-xs font-bold text-slate-700 dark:text-slate-300">
                  Horários em <strong class="text-slate-900 dark:text-white font-mono">{{ rescheduleForm.targetDate }}</strong>:
                </span>
                <span class="text-xs font-semibold text-slate-500 dark:text-slate-400">
                  {{ selectedDaySlots.filter(s => s.available).length }} vaga(s) livre(s)
                </span>
              </div>

              <!-- Lista de Pílulas de Horários -->
              <div v-if="selectedDaySlots.length === 0" class="py-12 text-center text-xs text-slate-500">
                Nenhum horário disponível para esta data.
              </div>

              <div v-else class="grid grid-cols-2 gap-2 max-h-[230px] overflow-y-auto pr-1">
                <button
                  v-for="slot in selectedDaySlots"
                  :key="slot.time_label"
                  type="button"
                  class="p-2.5 rounded-xl text-xs font-mono transition-all flex items-center justify-between border cursor-pointer"
                  :class="[
                    rescheduleForm.targetTime === slot.time_label && slot.available
                      ? 'bg-blue-600 text-white font-black border-blue-400 shadow-md ring-2 ring-blue-500/30'
                      : !slot.available || slot.status === 'full' || slot.blocked
                        ? 'bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900/40 text-rose-600 dark:text-rose-400 opacity-70 cursor-not-allowed line-through'
                        : slot.booked > 0
                          ? 'bg-amber-50 dark:bg-slate-900 hover:bg-amber-100 dark:hover:bg-slate-800 border-amber-300 dark:border-amber-500/40 text-amber-800 dark:text-amber-300 font-bold'
                          : 'bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 text-slate-800 dark:text-slate-200'
                  ]"
                  :disabled="!slot.available"
                  @click="rescheduleForm.targetTime = slot.time_label"
                >
                  <span class="font-bold">{{ slot.time_label }}</span>
                  <span
                    v-if="slot.status === 'full' || !slot.available"
                    class="text-[10px] font-sans font-normal"
                  >
                    (Lotado)
                  </span>
                  <span
                    v-else-if="slot.blocked"
                    class="text-[10px] font-sans font-normal"
                  >
                    (Bloqueio)
                  </span>
                  <span
                    v-else
                    class="text-[10px] font-sans"
                    :class="rescheduleForm.targetTime === slot.time_label ? 'text-blue-100' : 'text-slate-500 dark:text-slate-400'"
                  >
                    ({{ slot.free_slots }} vaga{{ slot.free_slots > 1 ? 's' : '' }})
                  </span>
                </button>
              </div>
            </div>

            <!-- Resumo e Confirmação -->
            <div class="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div class="text-xs text-slate-600 dark:text-slate-300">
                Nova Data: <strong class="text-blue-600 dark:text-blue-400 font-mono">{{ rescheduleForm.targetDate }} às {{ rescheduleForm.targetTime }}</strong>
              </div>
              <button
                type="button"
                class="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl transition-all cursor-pointer shadow-md disabled:opacity-50 flex items-center gap-1.5"
                :disabled="isRescheduling || !rescheduleForm.targetDate || !rescheduleForm.targetTime"
                @click="confirmReschedule"
              >
                <span>{{ isRescheduling ? 'Reagendando...' : 'Confirmar Reagendamento' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
